import { renderTemplate } from "../../templateRenderer";
import { CLASSIC_TEMPLATE, CLASSIC_CSS } from "./loader";
import { IMAGE_URL } from "../../../../Constant";
import type { PageOrientation } from "../../types";

const CLASSIC_PAGE_SIZES = {
  A4: {
    width: "210mm",
    height: "297mm",
    orientation: "portrait" as const,
    pad: "18mm",
    font: "13px",
  },
  A5: {
    width: "148mm",
    height: "210mm",
    orientation: "portrait" as const,
    pad: "10mm",
    font: "11px",
  },
  A3: {
    width: "297mm",
    height: "420mm",
    orientation: "portrait" as const,
    pad: "24mm",
    font: "16px",
  },
  "80mm": {
    width: "78mm",
    height: "auto",
    orientation: "portrait" as const,
    pad: "4mm",
    font: "14px",
  },
} as const;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(
  pageSize: string,
  isForPrint: boolean,
  orientation?: PageOrientation,
): string {
  const is80 = pageSize === "80mm";
  const sz =
    CLASSIC_PAGE_SIZES[pageSize as keyof typeof CLASSIC_PAGE_SIZES] ||
    CLASSIC_PAGE_SIZES.A4;

  const isLandscape = orientation === "landscape";
  const width = isLandscape && !is80 ? sz.height : sz.width;
  const height = isLandscape && !is80 ? sz.width : sz.height;

  return `
    :root {
      --inv-font:       ${sz.font};
      --inv-width:      ${width};
      --inv-height:     ${height};
      --inv-pad:        ${isForPrint ? (is80 ? "0" : "10mm") : sz.pad};
      --inv-bg:         #fff;
      --inv-body-pad:   ${isForPrint ? "0" : is80 ? "8px 4px" : "24px 16px"};
      --inv-h2-size:    ${is80 ? "1.1em" : "1.25em"};
      --inv-grand-size: ${is80 ? "1em" : "1.05em"};
      --inv-ftr-mt:     ${is80 ? "10px" : "18px"};
      --inv-ftr-size:   ${is80 ? "0.78em" : "0.82em"};
      --inv-page-size:  ${is80 ? "80mm auto" : pageSize};
      --inv-orientation: ${is80 ? "portrait" : orientation || sz.orientation};
    }
  `;
}

export interface ReturnInvoiceTemplateContext {
  orderReturn: any;
  config: any;
  pageSize: "A4" | "A5" | "A3" | "80mm";
  orientation?: PageOrientation;
  isForPrint?: boolean;
  creditOnly?: boolean;
}

export function renderReturnClassicTemplate(
  ctx: ReturnInvoiceTemplateContext,
): string {
  const {
    orderReturn,
    config,
    pageSize,
    isForPrint = false,
    creditOnly = false,
    orientation = "portrait",
  } = ctx;

  const currency = config.currency || "LKR";
  const allItems: any[] = orderReturn.items || [];
  const dateObj = new Date(orderReturn.return_date || orderReturn.created_at);

  const productItems = allItems.map((i: any) => ({
    product_name: i.product_name || "Product",
    sku_code: i.sku_code || "",
    quantity: parseFloat(i.quantity || 0),
    price: parseFloat(i.refund_price || 0),
    total_price: parseFloat(i.total_refund || 0),
    batch_no: i.return_type_name || "",
  }));

  const productSubtotal = productItems.reduce(
    (sum: number, i: any) => sum + i.total_price,
    0,
  );

  const dateStr = dateObj.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  const timeStr = dateObj.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const cashier = orderReturn.username || "";
  const logoUrl = `${IMAGE_URL}/${config.logo_url}`;

  // If creditOnly note: show only the credit amount (exchange_value) as total
  const refundedValue = parseFloat(orderReturn.total_refunded || 0);
  const creditAmount = parseFloat(orderReturn.exchange_value || 0);
  const documentTotal = creditOnly ? creditAmount : refundedValue;

  const contentContext = {
    storeName: config.store_name || "POS System",
    address: config.address || "",
    phone: config.phone || "",
    email: config.email || "",
    whatsapp: config.whatsapp || "",
    website: config.website || "",
    logoUrl,
    footer: config.receipt_footer || "Thank you for shopping with us!",
    orderStatus: creditOnly
      ? "STORE CREDIT"
      : orderReturn.refund_type_name || "RETURNED",
    orderNo: orderReturn.return_no || "",
    originalOrderNo: orderReturn.order_no || "",
    dateStr,
    timeStr,
    cashier,
    customerName: orderReturn.customer_name || "Walk-in Customer",
    customerPhone: orderReturn.customer_phone || "",
    customerAddress: orderReturn.customer_address || "",
    customerEmail: orderReturn.customer_email || "",
    currency,
    orderNote: orderReturn.note || "",
    productItems,
    productSubtotal,
    totalItemsCount: productItems.length,
    refundMethodName: orderReturn.refund_payment_method_name || "",
    grandTotal: documentTotal,
    cashBalanceAmount: parseFloat(orderReturn.cash_balance_amount || 0),
    storeCreditAmount: parseFloat(orderReturn.store_credit_amount || 0),
    isA4: pageSize === "A4" || pageSize === "A3" || pageSize === "A5",
    pageSize,
  };

  const content = renderTemplate(CLASSIC_TEMPLATE, contentContext);
  const styles =
    buildCssVariables(pageSize, isForPrint, orientation) + "\n" + CLASSIC_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `${creditOnly ? "Store Credit" : "Order Return"} - ${orderReturn.return_no}`,
    styles,
    content,
  });
}
